import React, { useMemo, useState } from "react";
import { Button, DatePicker, Input, InputNumber, Popconfirm, Table, Typography } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { BackendScheduleRow } from "./types";
import { ScheduleRowInput } from "./creditsService";

const { Text } = Typography;

type EditableRow = {
  _key: string;
  date: string;
  disbursement: number;
  otherDisbursements: number;
  principalPaid: number;
  interestPaid: number;
  otherPayments: number;
  description: string;
};

type Props = {
  initialSchedule: BackendScheduleRow[];
  saving?: boolean;
  onSave: (schedule: ScheduleRowInput[]) => void;
  onCancel: () => void;
};

let keyCounter = 0;
const nextKey = () => `row-${Date.now()}-${keyCounter++}`;

const toEditableRow = (row: BackendScheduleRow): EditableRow => ({
  _key: nextKey(),
  date: row.dueDate,
  disbursement: row.disbursement || 0,
  otherDisbursements: row.otherDisbursements || 0,
  principalPaid: row.principalPaid || 0,
  interestPaid: row.interestPaid || 0,
  otherPayments: row.otherPayments || 0,
  description: row.description || "",
});

const emptyRow = (): EditableRow => ({
  _key: nextKey(),
  date: dayjs().format("YYYY-MM-DD"),
  disbursement: 0,
  otherDisbursements: 0,
  principalPaid: 0,
  interestPaid: 0,
  otherPayments: 0,
  description: "",
});

const AmortizationScheduleEditor: React.FC<Props> = ({
  initialSchedule,
  saving,
  onSave,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<EditableRow[]>(() =>
    initialSchedule.length ? initialSchedule.map(toEditableRow) : [emptyRow()],
  );

  const basePeriod = initialSchedule[0]?.periodIndex ?? 0;

  const money = (val: number) =>
    (val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const computed = useMemo(() => {
    let runningBalance = 0;
    return rows.map((row, index) => {
      runningBalance =
        runningBalance + row.disbursement + row.otherDisbursements - row.principalPaid;
      return {
        ...row,
        period: basePeriod + index,
        remainingPrincipal: Number(runningBalance.toFixed(2)),
        installment: row.principalPaid + row.interestPaid,
      };
    });
  }, [rows, basePeriod]);

  const updateRow = (key: string, patch: Partial<EditableRow>) => {
    setRows((prev) => prev.map((r) => (r._key === key ? { ...r, ...patch } : r)));
  };

  const handleInsertBelow = (index: number) => {
    setRows((prev) => {
      const next = [...prev];
      next.splice(index + 1, 0, emptyRow());
      return next;
    });
  };

  const handleDelete = (key: string) => {
    setRows((prev) => (prev.length > 1 ? prev.filter((r) => r._key !== key) : prev));
  };

  const handleSave = () => {
    const schedule: ScheduleRowInput[] = computed.map((row) => ({
      period: row.period,
      date: row.date,
      disbursement: row.disbursement,
      otherDisbursements: row.otherDisbursements,
      principalPaid: row.principalPaid,
      interestPaid: row.interestPaid,
      otherPayments: row.otherPayments,
      remainingPrincipal: row.remainingPrincipal,
      description: row.description,
    }));
    onSave(schedule);
  };

  const columns = [
    {
      title: t("Period"),
      dataIndex: "period",
      key: "period",
      width: 60,
      fixed: "left" as const,
    },
    {
      title: t("Date"),
      dataIndex: "date",
      key: "date",
      width: 140,
      render: (_: string, row: (typeof computed)[number]) => (
        <DatePicker
          size="small"
          format="DD.MM.YYYY"
          value={row.date ? dayjs(row.date) : null}
          onChange={(d) => updateRow(row._key, { date: d ? d.format("YYYY-MM-DD") : "" })}
          allowClear={false}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: t("Disbursement"),
      dataIndex: "disbursement",
      key: "disbursement",
      width: 140,
      render: (_: number, row: (typeof computed)[number]) => (
        <InputNumber
          size="small"
          value={row.disbursement}
          onChange={(v) => updateRow(row._key, { disbursement: Number(v) || 0 })}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: t("Principal"),
      dataIndex: "principalPaid",
      key: "principalPaid",
      width: 130,
      render: (_: number, row: (typeof computed)[number]) => (
        <InputNumber
          size="small"
          value={row.principalPaid}
          onChange={(v) => updateRow(row._key, { principalPaid: Number(v) || 0 })}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: t("Interest"),
      dataIndex: "interestPaid",
      key: "interestPaid",
      width: 130,
      render: (_: number, row: (typeof computed)[number]) => (
        <InputNumber
          size="small"
          value={row.interestPaid}
          onChange={(v) => updateRow(row._key, { interestPaid: Number(v) || 0 })}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: t("Installment"),
      dataIndex: "installment",
      key: "installment",
      width: 120,
      render: (val: number) => <Text strong>{money(val)}</Text>,
    },
    {
      title: t("Other costs"),
      dataIndex: "otherPayments",
      key: "otherPayments",
      width: 130,
      render: (_: number, row: (typeof computed)[number]) => (
        <InputNumber
          size="small"
          value={row.otherPayments}
          onChange={(v) => updateRow(row._key, { otherPayments: Number(v) || 0 })}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: t("Remaining"),
      dataIndex: "remainingPrincipal",
      key: "remainingPrincipal",
      width: 140,
      render: (val: number) => <Text strong>{money(val)}</Text>,
    },
    {
      title: t("Description"),
      dataIndex: "description",
      key: "description",
      width: 200,
      render: (_: string, row: (typeof computed)[number]) => (
        <Input
          size="small"
          value={row.description}
          onChange={(e) => updateRow(row._key, { description: e.target.value })}
        />
      ),
    },
    {
      title: "",
      key: "actions",
      width: 90,
      fixed: "right" as const,
      render: (_: unknown, row: (typeof computed)[number], index: number) => (
        <div style={{ display: "flex", gap: 4 }}>
          <Button
            size="small"
            icon={<PlusOutlined />}
            onClick={() => handleInsertBelow(index)}
            title={t("Insert row below")}
          />
          <Popconfirm
            title={t("Delete this row?")}
            onConfirm={() => handleDelete(row._key)}
            disabled={rows.length <= 1}
          >
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              disabled={rows.length <= 1}
              title={t("Delete row")}
            />
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div>
      <Table
        dataSource={computed}
        columns={columns}
        rowKey="_key"
        size="small"
        pagination={false}
        scroll={{ x: 1100, y: 360 }}
        bordered
      />
      <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <Button onClick={onCancel} disabled={saving}>
          {t("Cancel")}
        </Button>
        <Button type="primary" loading={saving} onClick={handleSave}>
          {t("Save schedule")}
        </Button>
      </div>
    </div>
  );
};

export default AmortizationScheduleEditor;
